import React, { useState } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { MediaFilter } from "@/components/media/MediaFilter";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MediaModel, MemoryModel, Contact, GroupModel } from "common247";
import { IFilteredMediaResultSet } from "@/services/media-filter-service";

const SAMPLE_MEDIA: MediaModel[] = [
  {
    id: "med-1",
    title: "Archipelago Sunset",
    name: "archipelago-sunset.jpg",
    contentType: "image/jpeg",
    mediaCategory: "standard",
    mediaType: "image",
    favouriteRating: 5,
    createdAt: 1782000000000,
    contactIds: ["c-alice", "c-bob"],
    groupIds: ["g-family"],
    memoryIds: ["mem-1"],
    tags: {
      "t-travel": { id: "t-travel", value: "travel", addedAt: 1782000000000, addedBy: "user-1" },
      "t-summer": { id: "t-summer", value: "summer", addedAt: 1782000000000, addedBy: "user-1" },
    },
  } as any,
  {
    id: "med-2",
    title: "Skiing Downhill Run",
    name: "skiing-downhill.mp4",
    contentType: "video/mp4",
    mediaCategory: "standard",
    mediaType: "video",
    favouriteRating: 4,
    createdAt: 1797200000000,
    contactIds: ["c-bob"],
    groupIds: ["g-friends"],
    memoryIds: ["mem-2"],
    tags: {
      "t-sports": { id: "t-sports", value: "sports", addedAt: 1797200000000, addedBy: "user-1" },
    },
  } as any,
  {
    id: "med-3",
    title: "Studio Jam Acoustic Melody",
    name: "jazz-solo.mp3",
    contentType: "audio/mpeg",
    mediaCategory: "generated",
    mediaType: "audio",
    favouriteRating: 3,
    createdAt: 1791800000000,
    contactIds: ["c-charlie"],
    groupIds: ["g-friends"],
    memoryIds: ["mem-4"],
    tags: {
      "t-music": { id: "t-music", value: "music", addedAt: 1791800000000, addedBy: "user-1" },
    },
  } as any,
  {
    id: "med-4",
    title: "Heirloom Genealogy Record",
    name: "family-tree-record.pdf",
    contentType: "application/pdf",
    mediaCategory: "documents",
    mediaType: "pdf",
    favouriteRating: 5,
    createdAt: 1767600000000,
    contactIds: ["c-alice"],
    groupIds: ["g-family"],
    memoryIds: ["mem-3"],
    tags: {
      "t-history": { id: "t-history", value: "history", addedAt: 1767600000000, addedBy: "user-1" },
    },
  } as any,
];

const SAMPLE_MEMORIES: MemoryModel[] = [
  {
    id: "mem-1",
    title: "Stockholm Archipelago Midsummer",
    description: "Sailing across the islands with family.",
    date: "20-Jun-2026 to 24-Jun-2026",
    contactIds: ["c-alice", "c-bob"],
    groupIds: ["g-family"],
  } as any,
  {
    id: "mem-2",
    title: "Alpine Skiing Expedition",
    description: "Powder snow runs in the mountains.",
    date: "15-Dec-2026 to 18-Dec-2026",
    contactIds: ["c-bob"],
    groupIds: ["g-friends"],
  } as any,
  {
    id: "mem-3",
    title: "Heirloom Reading Evening",
    description: "Archival poetry and ancestry documentation.",
    date: "05-Jan-2026",
    contactIds: ["c-alice"],
    groupIds: ["g-family"],
  } as any,
  {
    id: "mem-4",
    title: "Autumn Jazz Recording",
    description: "Studio session capturing live melodies.",
    date: "12-Oct-2026",
    contactIds: ["c-charlie"],
    groupIds: ["g-friends"],
  } as any,
];

const SAMPLE_CONTACTS: Contact[] = [
  { id: "c-alice", name: "Alice Wonderland", email: "alice@example.com" } as any,
  { id: "c-bob", name: "Bob Builder", email: "bob@example.com" } as any,
  { id: "c-charlie", name: "Charlie Chaplin", email: "charlie@example.com" } as any,
];

const SAMPLE_GROUPS: GroupModel[] = [
  { id: "g-family", name: "Wonderland Family", description: "Close relatives" } as any,
  { id: "g-friends", name: "Ski & Sports Circle", description: "Adventure crew" } as any,
];

/**
 * Workbench test harness for MediaFilter component.
 */
export default function MediaFilterWorkbenchScreen() {
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");
  const textMuted = useThemeColor({}, "textMuted");

  const [filteredResults, setFilteredResults] = useState<IFilteredMediaResultSet>({
    media: SAMPLE_MEDIA,
    memories: SAMPLE_MEMORIES,
    contacts: SAMPLE_CONTACTS,
    groups: SAMPLE_GROUPS,
    totalCounts: {
      media: SAMPLE_MEDIA.length,
      memories: SAMPLE_MEMORIES.length,
      contacts: SAMPLE_CONTACTS.length,
      groups: SAMPLE_GROUPS.length,
    },
    criteria: {},
  });

  return (
    <ThemedScreen
      title="Media Filter Workbench"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedPanel padding={14} elevation="low" style={{ marginBottom: 12 }}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 16, marginBottom: 4 }}>
            MediaFilter Interactive Harness
          </ThemedText>
          <ThemedText style={{ color: textMuted, fontSize: 13 }}>
            Filters across media types, categories, favourite ratings, tags, memory links, contacts, and date intervals.
          </ThemedText>
        </ThemedPanel>

        {/* MediaFilter Component */}
        <MediaFilter
          allMedia={SAMPLE_MEDIA}
          allMemories={SAMPLE_MEMORIES}
          allContacts={SAMPLE_CONTACTS}
          allGroups={SAMPLE_GROUPS}
          onFilteredResultsChange={setFilteredResults}
        />

        {/* Results Live Inspector */}
        <ThemedPanel padding={14} elevation="low" style={{ marginTop: 12 }}>
          <ThemedText type="defaultSemiBold" style={{ fontSize: 14, marginBottom: 8 }}>
            Filtered Results ({filteredResults.totalCounts.media} media found)
          </ThemedText>

          <View style={{ gap: 8 }}>
            {filteredResults.media.length === 0 ? (
              <ThemedText style={{ color: textMuted, fontStyle: "italic", paddingVertical: 12 }}>
                No media items match the current filter criteria.
              </ThemedText>
            ) : (
              filteredResults.media.map((m) => (
                <View
                  key={m.id}
                  style={[styles.resultItem, { backgroundColor: badgeBg }]}
                >
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <EntityIcon type="media" size={16} color={tintColor} />
                    <View style={{ flex: 1 }}>
                      <ThemedText style={{ fontWeight: "700", fontSize: 13 }}>
                        {m.title || m.name}
                      </ThemedText>
                      <ThemedText style={{ color: textMuted, fontSize: 11 }}>
                        Type: {m.mediaType || m.contentType} • Cat: {m.mediaCategory || "standard"} • Rating: {m.favouriteRating || 3}★
                      </ThemedText>
                    </View>
                  </View>
                </View>
              ))
            )}
          </View>
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 14,
  },
  resultItem: {
    padding: 10,
    borderRadius: 4,
  },
});
